import { fetchApi } from "@/lib/api-helpers";
import { z } from "zod";
import { cartResponseSchema } from "./carts.api";

export const customerInfoSchema = z.object({
  customer: z.object({
    id: z.number(),
    account_id: z.string(),
    active_subscriptions_count: z.number(),
    addresses: z.array(
      z.object({
        id: z.number(),
        address1: z.string(),
        address2: z.string().nullable(),
        address3: z.string().nullable(),
        city: z.string(),
        country_code: z.string(),
        default: z.boolean(),
        name: z.string(),
        postal_code: z.string(),
        state: z.string(),
        subdivision_code: z.string(),
      }),
    ),
    country: z.null().optional(),
    created_at: z.string(),
    customer_notes: z.any(),
    default_address: z.null().optional(),
    default_address_id: z.null().optional(),
    display_total_spent: z.string(),
    email: z.string().email(),
    // TODO: this should not be nullable from API
    first_name: z.string().nullable(),
    full_name: z.string(),
    inactive_subscriptions_count: z.number(),
    // TODO: this should not be nullable from API
    last_name: z.string().nullable(),
    last_order_id: z.null(),
    notes: z.null(),
    orders_count: z.number(),
    payment_methods: z.array(
      z.object({
        id: z.number(),
        company_id: z.null(),
        created_at: z.null(),
        payload: z.null(),
        source: z.null(),
        updated_at: z.null(),
        user_id: z.null(),
      }),
    ),
    total_spent: z.number(),
    verified_email: z.boolean(),
  }),
  meta: z.object({
    decoded_jwt: z.object({
      auth_type: z.string(),
      customer_id: z.number(),
      full_name: z.string(),
      email: z.string().email(),
      exp: z.number(),
    }),
  }),
});

export type CustomerInfo = z.infer<typeof customerInfoSchema>;

export interface UserInfoTranslations {
  firstNameRequired?: string;
  lastNameRequired?: string;
  languageRequired?: string;
}

export const createUserInfoSchema = (translations?: UserInfoTranslations) =>
  z.object({
    first_name: z
      .string()
      .min(1, translations?.firstNameRequired ?? "First name is required"),
    last_name: z
      .string()
      .min(1, translations?.lastNameRequired ?? "Last name is required"),
    phone_number: z.string().optional(),
    language: z
      .string()
      .min(1, translations?.languageRequired ?? "Language is required"),
  });

// default for backward compatibility
export const userInfoSchema = createUserInfoSchema();

export type UserInfo = z.infer<typeof userInfoSchema>;

const customerAccountSchema = z.object({
  fluid_pay_account: z.object({
    id: z.number(),
    email: z.string().email(),
    address_count: z.number(),
    payment_methods_count: z.number(),
    language_iso: z.string().nullable().default("en"),
  }),
  customer: z.object({
    id: z.number(),
    active: z.boolean(),
    active_subscriptions_count: z.number(),
    display_total_spent: z.string(),
    email: z.string().email(),
    first_name: z.string(),
    full_name: z.string(),
    inactive_subscriptions_count: z.number(),
    is_rep: z.boolean(),
    phone: z.string().nullable(),
    orders_count: z.number(),
  }),
});

export type CustomerAccount = z.infer<typeof customerAccountSchema>;

const fluidPayAccountSchema = z.object({
  id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email(),
  phone: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  settings: z.record(z.string(), z.unknown()).nullable(),
  slug: z.string(),
  language: z.record(z.string(), z.unknown()),
  default_address: z.record(z.string(), z.unknown()),
  default_payment_method: z.record(z.string(), z.unknown()),
});

const fluidPayAccountResponseSchema = z.object({
  fluid_pay_account: fluidPayAccountSchema,
  meta: z.object({
    request_id: z.string().uuid(),
    timestamp: z.string().datetime(),
  }),
});

export type FluidPayAccount = z.infer<typeof fluidPayAccountSchema>;
export type FluidPayAccountResponse = z.infer<
  typeof fluidPayAccountResponseSchema
>;

export async function fetchCustomerInfo(cartToken: string, jwt: string) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/me?jwt=${jwt}`,
    method: "GET",
    outputSchema: customerInfoSchema,
  });
}

export async function logoutCustomer({ cartToken }: { cartToken: string }) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/logout`,
    method: "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function fetchCustomerAccount(jwt: string) {
  return fetchApi({
    endpoint: `/fluid_pay/me?jwt=${jwt}`,
    method: "GET",
    outputSchema: customerAccountSchema,
  });
}

export async function updateCustomer(
  jwt: string,
  body: {
    first_name: string;
    last_name: string;
    phone?: string | null;
    email?: string;
    language_iso: string;
  },
) {
  return fetchApi({
    endpoint: `/fluid_pay/update_me?jwt=${jwt}`,
    method: "PATCH",
    input: { fluid_pay_account: body },
    outputSchema: fluidPayAccountResponseSchema,
  });
}
